import { useMemo } from 'react'

import { useQuery } from '@percent/utility'

import { useInsightsContext } from '../context/InsightsContext'
import {
  filterFieldKeys,
  FilterValues,
  ResolvedFilter,
} from '../types/insights.types'
import { deserialiseFilters, hasActiveValues } from '../utils/filterSerialiser'

// Filter definitions are stable for a session, so cache them for a minute.
const FILTERS_STALE_TIME_MS = 60_000

export type UseFiltersResult = {
  /** Definitions (label, valueType, resolved option values/range) from `/filters`. */
  filterDefs: ResolvedFilter[]
  /** Current, typed filter values (backed by the provider's filter adapter). */
  values: FilterValues
  setValue: (key: string, value: string | string[] | undefined) => void
  setValues: (next: FilterValues) => void
  reset: () => void
  hasActiveFilters: boolean
  /** Domain-locked values (always applied, controls hidden); see `<Insights.Domain lockedFilters>`. */
  lockedFilters: FilterValues
  isLoadingDefs: boolean
  defsError: unknown
}

/**
 * The centralised filter hook for the current insights domain (from
 * `<Insights.Domain>`): fetches the domain's filter definitions and manages their
 * values through the provider's adapter (URL by default). Returns both the
 * definitions (to render controls) and the live values (to pass to a card/chart).
 */
export const useFilters = (): UseFiltersResult => {
  const { domain, service, filterAdapter, lockedFilters } = useInsightsContext()

  const { data, isLoading, error } = useQuery(
    [`insights/${domain}/filters`],
    service.getFilters,
    { domain },
    { staleTime: FILTERS_STALE_TIME_MS, retry: 1 }
  )

  const filterDefs = useMemo(() => data ?? [], [data])

  const fields = useMemo(() => {
    const specs = filterDefs.flatMap((def) =>
      filterFieldKeys(def).map((key) => ({
        key,
        multiple:
          key === def.key &&
          (def.valueType === 'stringArray' || def.valueType === 'enumArray'),
      }))
    )

    // De-dupe by key so overlapping/legacy defs don't register a field twice.
    return Array.from(new Map(specs.map((spec) => [spec.key, spec])).values())
  }, [filterDefs])

  const {
    values: rawValues,
    setValue,
    setValues,
    reset,
  } = filterAdapter.useFilterState(fields)

  // User-controllable values from the adapter (URL by default).
  const userValues = useMemo(
    () => deserialiseFilters(filterDefs, rawValues),
    [filterDefs, rawValues]
  )

  // Locked values always win and flow to every chart + the bar; merged on read
  // (not written to the adapter) so they need no URL param and survive Reset.
  const values = useMemo(
    () => ({ ...userValues, ...lockedFilters }),
    [userValues, lockedFilters]
  )

  // "Active" (and the Reset control) reflects only what the user can change —
  // a locked value isn't resettable, so it must not light up Reset on its own.
  const hasActiveFilters = useMemo(() => {
    const lockedKeys = new Set(Object.keys(lockedFilters))
    const controllable = Object.fromEntries(
      Object.entries(userValues).filter(([key]) => !lockedKeys.has(key))
    )

    return hasActiveValues(controllable)
  }, [userValues, lockedFilters])

  return {
    filterDefs,
    values,
    setValue,
    setValues,
    reset,
    hasActiveFilters,
    lockedFilters,
    isLoadingDefs: isLoading,
    defsError: error,
  }
}
